import { Canonical } from 'components/Basic/Head/Canonical/Canonical';
import { B2bFavicons } from 'components/Blocks/Favicons/B2bFavicons';
import { B2cAndWebFavicons } from 'components/Blocks/Favicons/B2cAndWebFavicons';
import CommonLayout from 'components/Layout/CommonLayout';
import { Error503 } from 'components/Pages/ErrorPage/503/Error503';
import PageContentProvider from 'context/PageContentProvider/PageContentProvider';
import ShopsysGlobalProvider from 'context/ShopsysGlobalProvider/ShopsysGlobalProvider';
import { extend, locale } from 'dayjs';
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/hu';
import 'dayjs/locale/it';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/ru';
import 'dayjs/locale/sk';
import 'dayjs/locale/zh';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { ServerSidePropsType } from 'helpers/InitServerSideProps';
import { B2C_CZ_DOMAIN_ID, isB2bDomain, isDomain } from 'helpers/isDomainLocale';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import i18nConfig from 'i18n';
import appWithI18n from 'next-translate/appWithI18n';
import { AppProps as NextAppProps } from 'next/app';
import { NextComponentType, NextPageContext } from 'next/dist/shared/lib/utils';
import dynamic from 'next/dynamic';
import { Roboto } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import 'styles/globals.css';
import { NextPageCustomProps } from 'types/global';
import getGtmHeadScript from 'utils/Gtm/GtmHeadScript';

const UrqlWrapper = dynamic(() => import('components/Layout/UrqlWrapper').then((component) => component.UrqlWrapper));
const SupportChat = dynamic(
    () => import('components/Blocks/SupportChat/SupportChat').then((component) => component.SupportChat),
    { ssr: false },
);

extend(LocalizedFormat);

const robotoFont = Roboto({
    weight: ['400', '500', '700'],
    subsets: ['latin-ext', 'latin'],
    variable: '--font-roboto',
});

type ErrorProps = { err?: any };

type CustomAppComponentProps = NextComponentType<NextPageContext, any, any> & NextPageCustomProps;

type CustomNextAppProps = {
    Component: CustomAppComponentProps;
};

type AppProps = {
    pageProps: ServerSidePropsType;
} & Omit<NextAppProps<ErrorProps>, 'Component' | 'pageProps'> &
    ErrorProps &
    CustomNextAppProps;

function MyApp({ Component, pageProps, err }: AppProps): ReactElement {
    const router = useRouter();
    const displaySupportChat = isDomain(pageProps.domainConfig.domainId, B2C_CZ_DOMAIN_ID);

    locale(pageProps.domainConfig.defaultLocale);

    useBroadcastChannel({
        name: 'reloadPage',
        callBack: () => {
            router.reload();
        },
    });

    return (
        <>
            <Head key={isB2bDomain(pageProps.domainConfig) ? 'b2b' : 'b2c'}>
                <meta name="apple-mobile-web-app-title" content="Tescoma" />
                <meta name="application-name" content="Tescoma" />
                <meta name="theme-color" content="#bd0d2e" />
                <meta name="msapplication-TileColor" content="#bd0d2e" />

                {isB2bDomain(pageProps.domainConfig) ? (
                    <>
                        <meta name="robots" content="noindex,nofollow" />
                        <B2bFavicons key="b2b-favicons" />
                    </>
                ) : (
                    <B2cAndWebFavicons key="b2c-web-favicons" />
                )}
            </Head>
            {getGtmHeadScript()} {displaySupportChat && <SupportChat />}
            <style jsx global>{`
                html {
                    font-family: ${robotoFont.style.fontFamily};
                }
            `}</style>
            <Canonical url={pageProps.domainConfig.url} />
            <UrqlWrapper pageProps={pageProps}>
                <ShopsysGlobalProvider>
                    <PageContentProvider pageProps={pageProps}>
                        {Component.layout === 'plain' ? (
                            <Component {...pageProps} err={err} />
                        ) : (
                            <CommonLayout showLastVisitedProducts={Component.layout === 'withLastVisitedProducts'}>
                                {pageProps.isMaintenance ? <Error503 /> : <Component {...pageProps} err={err} />}
                            </CommonLayout>
                        )}
                    </PageContentProvider>
                </ShopsysGlobalProvider>
            </UrqlWrapper>
        </>
    );
}

// eslint-disable-next-line
// @ts-ignore
export default appWithI18n(MyApp, { ...i18nConfig });
